import {
  ChatComponent,
  ChatComponentRef
} from "@fidget/common/components/chat/chatComponent/chatComponent";
import { ChatContactEnquiry } from "@fidget/common/components/chat/chatContactEnquiry";
import { CloserLoaderComponent } from "@fidget/common/components/loader/closerLoader";
import { ChatNotification } from "@fidget/common/components/mstchat/chatNotification";
import { DateSeparator } from "@fidget/common/components/mstchat/dateSeparator";
import { NotificationComponent } from "@fidget/common/components/notificationComponent";
import { TypingComponent } from "@fidget/common/components/typing";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { isPredefinedMessageConfigContext } from "@fidget/common/models/shared/spinner/types/messageWidgets/WidgetFormSubmissionWithWidget";
import { assertUnreachable } from "@fidget/common/utils/ts-utils";
import classNames from "classnames";
import { observer } from "mobx-react";
import React, { MutableRefObject, useRef } from "react";
import { useStore } from "../../models";
import { ChatItemAssertion } from "../../models/room/chat";
import { IChatItem } from "../../models/room/chat/ChatItem";
import { IRoom } from "../../models/room/Room";
import styles from "./AgentChat.css";
import { ChatLimit } from "./chatLimit";
import { ChatMessage } from "./ChatMessage";
import { ChatNote } from "./chatNote";
import { ChatSection } from "./chatSection";
import { ChatStatusNotification } from "./chatStatusNotification";
import { CTANotification } from "./ctaNotification";
import { Meeting } from "./Meeting";
import { MeetingScheduler } from "./MeetingScheduler";
import { MessageTypingPreview } from "./MessageTypingPreview";
import { RoomSummary } from "./roomSummary/roomSummary";
import { ChatEventFormNotification } from "./WidgetFormEventWrapper";

interface Props {
  containerClassName?: string;
  room: IRoom;
  scrollContainerRef: MutableRefObject<ChatComponentRef | undefined>;
}

export const AgentChat = observer(({ containerClassName, room, scrollContainerRef }: Props) => {
  const { currentUserStore, uiStore, usersStore } = useStore();
  const scrollContainer = useRef<ChatComponentRef>();

  const renderItem = (item: IChatItem) => {
    const { id, timestamp } = item;

    if (ChatItemAssertion.isGroup(item)) {
      const onUndeliveredClick = (messageId: string) =>
        uiStore.setChatNotification({
          message: intl.formatMessage("conversation.error.couldntSend.title"),
          onAction: () => {
            const message = room.chat.removeUndeliveredMessage(messageId);
            if (message && ChatItemAssertion.isUserText(message)) {
              room.sendMessage(message.text, message.attachments);
            }
          },
          actionText: intl.formatMessage("conversation.error.couldntSend.action"),
          onCancel: () => room.chat.removeUndeliveredMessage(messageId)
        });

      return (
        <ChatSection
          key={id}
          item={item}
          itemRenderer={renderItem}
          onUndeliveredClick={onUndeliveredClick}
        />
      );
    }
    if (ChatItemAssertion.isFakeUserText(item)) {
      return <ChatMessage key={id} item={item} blurred />;
    }
    if (ChatItemAssertion.isUserText(item)) {
      return <ChatMessage key={id} item={item} />;
    }
    if (
      ChatItemAssertion.isNotification(item) ||
      ChatItemAssertion.isCallEvent(item) ||
      ChatItemAssertion.isMeetingEvent(item) ||
      ChatItemAssertion.isUserEvent(item) ||
      ChatItemAssertion.isCobrowsing(item)
    ) {
      return <ChatNotification key={id} message={item.text || ""} />;
    }
    if (ChatItemAssertion.isWidgetFormEvent(item)) {
      return isPredefinedMessageConfigContext(item.submission.widgetConfig) ? (
        <></>
      ) : (
        <ChatNotification key={id} message={<ChatEventFormNotification text={item.text || ""} />} />
      );
    }
    if (ChatItemAssertion.isTagsChange(item) || ChatItemAssertion.isTopicsChange(item)) {
      return <ChatNotification key={id} message={<ChatEventFormNotification text={item.text} />} />;
    }
    if (ChatItemAssertion.isCTAEvent(item)) {
      return <CTANotification key={id} message={item.text || ""} />;
    }
    if (ChatItemAssertion.isDateSeparator(item)) {
      return <DateSeparator key={id} timestamp={timestamp} />;
    }
    if (ChatItemAssertion.isNote(item)) {
      return <ChatNote key={id} user={item.user} timestamp={timestamp} text={item.text} />;
    }
    if (ChatItemAssertion.isMeeting(item)) {
      return <Meeting key={id} item={item} />;
    }
    if (ChatItemAssertion.isMeetingScheduler(item)) {
      return (
        <MeetingScheduler key={id} item={item} cancelScheduling={room.cancelMeetingScheduling} />
      );
    }
    if (ChatItemAssertion.isRoomSummary(item)) {
      return (
        <RoomSummary
          key={id}
          context={item.context}
          renderUser={(userId) => usersStore.getById(userId).displayedName}
        />
      );
    }
    if (ChatItemAssertion.isContactEnquiry(item)) {
      // XXX: onContactProvided will be supported in widget only
      return (
        <ChatContactEnquiry
          key={id}
          messageId={id}
          message={item.text}
          phoneRegion={currentUserStore.orgConfig?.phoneRegion}
        />
      );
    }
    if (ChatItemAssertion.isStatusChange(item)) {
      return (
        <ChatStatusNotification
          key={id}
          status={item.status}
          timestamp={timestamp}
          user={item.user}
          currentUser={currentUserStore.profile!}
        />
      );
    }
    if (ChatItemAssertion.isEmpty(item)) {
      return <React.Fragment key={id} />;
    }

    assertUnreachable(item);

    return <ChatNotification key={id} message="" />;
  };

  const renderChatNotification = () => {
    if (uiStore.chatNotification) {
      return (
        <NotificationComponent
          notification={uiStore.chatNotification}
          onDismiss={uiStore.removeChatNotification}
          dashboardStyle
        />
      );
    }

    return <ChatLimit hidden={!room.chat.hasFake} />;
  };

  const renderChatLoader = () => {
    return (
      <div className={classNames(styles.loader, { [styles.visible]: room.chat.isLoading })}>
        {<CloserLoaderComponent width={"40px"} height={"40px"} />}
      </div>
    );
  };

  const renderScrollButton = () => {
    const onClick = () => {
      if (scrollContainer.current) {
        scrollContainer.current.scrollToBottom();
      }
    };

    return (
      <div
        className={classNames(styles["scroll-button"], styles["scroll-to-bottom"])}
        onClick={onClick}
      >
        <i className={"icon-scroll-back-down"} />
      </div>
    );
  };

  const renderTypingUsers = () => {
    if (room.usersTypingEvents.length > 0) {
      return room.usersTypingEvents.map((typingEvent) => (
        <div key={typingEvent.profile.id} className={classNames(styles.typing)}>
          {typingEvent.preview !== undefined ? (
            <MessageTypingPreview userTypingEvent={typingEvent} />
          ) : (
            <TypingComponent users={room.usersTypingEvents.map((event) => event.profile)} />
          )}
        </div>
      ));
    }
    return undefined;
  };

  return (
    <ChatComponent
      containerClassName={containerClassName}
      messagesContainerClassName={styles.messages}
      notification={renderChatNotification()}
      loader={renderChatLoader()}
      scrollButton={renderScrollButton()}
      typingUsersComponent={renderTypingUsers()}
      items={room.chat.chatEntries}
      renderItem={renderItem}
      onScrolledToTop={room.chatTopReached}
      onIsScrolledToBottom={room.chatIsScrolledToBottom}
      ref={(e) => {
        scrollContainerRef.current = e || undefined;
        scrollContainer.current = e || undefined;
      }}
    />
  );
});
